import { ref, computed } from 'vue'
import { DatapointService } from '@/infrastructure/api/datapoint.service'
import { DATAPOINT_CODES } from '@/domain/entities/datapoint-codes'
import type { CountryRestriction } from '@/domain/entities/Datapoints'

// Module-level singleton state — shared across all callers of useDatapoints()
const enabledSet = ref<Set<string>>(new Set())
const loading = ref(false)
const datapointsReady = ref(false)
let fetchPromise: Promise<void> | null = null

// Auxiliary API state — populated from the DatapointsResponse payload.
// isOrganicUser is intentionally excluded — the backend does not ship that field;
// consumers must source it from useUserStore().isOrganicFreeUser.
const canPurchase = ref(false)
const canSetCRM = ref(false)
const isTrialUser = ref(false)
const isPlanExpired = ref(false)
const planExpireDate = ref<string | null>(null)
const isCRMExportDisabled = ref(false)
const userCanExportCRM = ref(false)
const userCanExportFile = ref(false)
const crmIsConnected = ref(false)
const isBulkDownloadEnabled = ref(false)
const restrictedCountries = ref<CountryRestriction[]>([])

function isEnabled(code: string): boolean {
  return enabledSet.value.has(code)
}

// Reset all module-level state — used by tests (beforeEach) and logout flows
export function resetDatapoints(): void {
  enabledSet.value = new Set()
  loading.value = false
  datapointsReady.value = false
  fetchPromise = null
  canPurchase.value = false
  canSetCRM.value = false
  isTrialUser.value = false
  isPlanExpired.value = false
  planExpireDate.value = null
  isCRMExportDisabled.value = false
  userCanExportCRM.value = false
  userCanExportFile.value = false
  crmIsConnected.value = false
  isBulkDownloadEnabled.value = false
  restrictedCountries.value = []
}

export function useDatapoints() {
  const service = new DatapointService()

  async function fetchDatapoints(): Promise<void> {
    if (fetchPromise) return fetchPromise
    if (datapointsReady.value) return
    loading.value = true
    fetchPromise = service.getDatapoints()
      .then((result) => {
        enabledSet.value = new Set(result.enabledDatapoints ?? [])
        canPurchase.value = result.canPurchase ?? false
        canSetCRM.value = result.canSetCRM ?? false
        isTrialUser.value = result.isTrialUser ?? false
        isPlanExpired.value = result.isPlanExpired ?? false
        planExpireDate.value = result.planExpireDate ?? null
        isCRMExportDisabled.value = result.isCRMExportDisabled ?? false
        userCanExportCRM.value = result.userCanExportCRM ?? false
        userCanExportFile.value = result.userCanExportFile ?? false
        crmIsConnected.value = result.crmIsConnected ?? false
        isBulkDownloadEnabled.value = result.isBulkDownloadEnabled ?? false
        restrictedCountries.value = result.restrictedCountries ?? []
        datapointsReady.value = true
      })
      .catch((e) => {
        console.warn('[useDatapoints] Failed to fetch datapoints:', e)
        // Error-path policy (§8.2):
        // COLD failure (never successfully loaded) → reset to defaults so callers
        // know no trustworthy state exists. WARM failure (prior datapointsReady === true)
        // → preserve last-known-good state (unreachable in practice because of the
        // early return at line 51; kept as forward-compat for a future forceRefreshDatapoints()).
        // NOT rethrowing — existing callers fire this fire-and-forget; rethrowing would
        // create unhandled promise rejections across the app (see CompanyDetailView.vue#L59).
        if (!datapointsReady.value) {
          enabledSet.value = new Set()
          canPurchase.value = false
          canSetCRM.value = false
          isTrialUser.value = false
          isPlanExpired.value = false
          planExpireDate.value = null
          isCRMExportDisabled.value = false
          userCanExportCRM.value = false
          userCanExportFile.value = false
          crmIsConnected.value = false
          isBulkDownloadEnabled.value = false
          restrictedCountries.value = []
        }
      })
      .finally(() => {
        loading.value = false
        fetchPromise = null
      })
    return fetchPromise
  }

  // "Enable" datapoints — true when present in enabledDatapoints
  const enablePhysicalLocation = computed(() => isEnabled(DATAPOINT_CODES.ENABLE_PHYSICAL_LOCATION_FILTER))
  const enableInternationalOffices = computed(() => isEnabled(DATAPOINT_CODES.ENABLE_INTERNATIONAL_OFFICES))
  const enableBrandFilters = computed(() => isEnabled(DATAPOINT_CODES.ENABLE_BRAND_FILTERS))
  const enableLocator = computed(() => isEnabled(DATAPOINT_CODES.IS_LOCATOR_ENABLED))
  const enableIndustry = computed(() => isEnabled(DATAPOINT_CODES.INDUSTRY_DATAPOINT))
  const enableSubIndustry = computed(() => isEnabled(DATAPOINT_CODES.SUB_INDUSTRY_DATAPOINT))
  const enableSic = computed(() => isEnabled(DATAPOINT_CODES.ENABLE_SIC_FILTER))
  const enableNaic = computed(() => isEnabled(DATAPOINT_CODES.ENABLE_NAICS_FILTER))
  const enableDirectDialMobile = computed(() => isEnabled(DATAPOINT_CODES.ENABLE_DIRECT_DIAL_MOBILE_FILTER))
  const enableContactEmailStatus = computed(() => isEnabled(DATAPOINT_CODES.ENABLE_CONTACT_EMAIL_STATUS_FILTER))
  const enableBusinessSizing = computed(() => isEnabled(DATAPOINT_CODES.ENABLE_BUSINESS_SIZING_FILTERS))
  const enableGlobalTraffic = computed(() => isEnabled(DATAPOINT_CODES.ENABLE_GLOBALTRAFFIC))
  const enableEcommerce = computed(() => isEnabled(DATAPOINT_CODES.ENABLE_E_COMMERCE_SEARCH))
  const enableShipping = computed(() => isEnabled(DATAPOINT_CODES.SHIPPING_DATAPOINT))
  const enableInternationalShipping = computed(() => isEnabled(DATAPOINT_CODES.ENABLE_INTERNATIONAL_SHIPPING))
  const enableMarketplaceSeller = computed(() => isEnabled(DATAPOINT_CODES.MARKETPLACE_SELLER_FILTER))
  const enableCloudHosting = computed(() => isEnabled(DATAPOINT_CODES.CLOUD_HOSTING_FILTER))
  const enableEmailHosting = computed(() => isEnabled(DATAPOINT_CODES.EMAIL_HOSTING_FILTER))
  const enablePayment = computed(() => isEnabled(DATAPOINT_CODES.PAYMENTS_DATAPOINT))
  const enableImportersExporters = computed(() => isEnabled(DATAPOINT_CODES.ENABLE_IMPORTERS_AND_EXPORTERS))
  const enableKeywordSearch = computed(() => isEnabled(DATAPOINT_CODES.KEYWORD_SEARCH))
  const enableAuthorizedProvider = computed(() => isEnabled(DATAPOINT_CODES.ENABLE_AUTHORIZED_PROVIDER))
  const enableBusinessModel = computed(() => isEnabled(DATAPOINT_CODES.BUSINESS_MODEL_FILTER))
  const enableCompaniesWithLocalizedSites = computed(() => isEnabled(DATAPOINT_CODES.ENABLE_COMPANIES_WITH_LOCALIZED_SITES))
  const enableCrmDataFilter = computed(() => isEnabled(DATAPOINT_CODES.ENABLE_CRM_DATA_FILTER))
  const enableSocialMedia = computed(() => isEnabled(DATAPOINT_CODES.ENABLE_SOCIAL_MEDIA_FILTERS))

  // CRM
  const enableVerifyCrmData = computed(() => isEnabled(DATAPOINT_CODES.ENABLE_VERIFY_CRM_DATA))

  // CRM Setup
  const disableCrmExport = computed(() => isEnabled(DATAPOINT_CODES.DISABLE_CRM_EXPORT))
  const enableCrmDedupOption = computed(() => isEnabled(DATAPOINT_CODES.ENABLE_CRM_DEDUP_OPTION))
  const enableIndustryMapping = computed(() => isEnabled(DATAPOINT_CODES.ENABLE_INDUSTRY_MAPPING))
  const enableSalesforceRefreshCompanies = computed(() => isEnabled(DATAPOINT_CODES.SALESFORCE_REFRESH_COMPANIES))
  const enableSalesforceRefreshContacts = computed(() => isEnabled(DATAPOINT_CODES.SALESFORCE_REFRESH_CONTACTS))
  const suppressContactData = computed(() => isEnabled(DATAPOINT_CODES.SUPPRESS_CONTACT_DATA))
  const enableHybridExport = computed(() => isEnabled(DATAPOINT_CODES.ENABLE_HYBRID_EXPORT))
  const enableVue3NewUi = computed(() => isEnabled(DATAPOINT_CODES.ENABLE_VUE3_NEW_UI))

  // Lead Enrichment
  const isDevTeam = computed(() => isEnabled(DATAPOINT_CODES.DEVELOPMENT_TEAM))
  const enableLeadEnrichment = computed(() => isEnabled(DATAPOINT_CODES.ENABLE_LEAD_ENRICHMENT))
  const enableLeadScoring = computed(() => isEnabled(DATAPOINT_CODES.ENABLE_LEAD_SCORING))

  // AI Search
  const enableAiSearch = computed(() => isEnabled(DATAPOINT_CODES.ENABLE_AI_SEARCH))

  // Navigation Gating
  const enableLookAlike = computed(() => isEnabled(DATAPOINT_CODES.ENABLE_LOOK_A_LIKE_SERVICE))
  // "Hide when TRUE" — consumers invert this (People Search is hidden while the flag is set).
  const hidePeopleSearch = computed(() => isEnabled(DATAPOINT_CODES.HIDE_PEOPLE_SEARCH))
  const enablePreferredTitles = computed(() => isEnabled(DATAPOINT_CODES.ENABLE_PREFERRED_TITLES))
  const enableExternalSofoApi = computed(() => isEnabled(DATAPOINT_CODES.ENABLE_EXTERNAL_SOFO_API))
  const enableBackOfficeIndex = computed(() => isEnabled(DATAPOINT_CODES.BACK_OFFICE_INDEX))

  // "Disable" datapoints — inverted: true when NOT present in enabledDatapoints
  const enableWebsiteTechnologies = computed(() => !isEnabled(DATAPOINT_CODES.DISABLE_WEB_SITE_TECHNOLOGIES))
  const enableLeadListFilters = computed(() => !isEnabled(DATAPOINT_CODES.DISABLE_LEADLIST_FILTERS))
  const enableLocalizedDomains = computed(() => !isEnabled(DATAPOINT_CODES.DISABLE_SHOW_LOCALIZED_DOMAINS))

  /**
   * True when the tenant has access to the Foreign Exchange filter.
   *
   * Historical note: Vue 2 had a bug where this was mapped to DISABLE_WEB_SITE_TECHNOLOGIES
   * (see datapointService.js line 195). Vue 3 intentionally replicated the bug until DEV-7125.
   * As of DEV-7125, this reads DISABLE_FOREIGN_EXCHANGE, matching the constant name.
   * If product reverts the decision, swap the code here — NO public API change.
   * DEPLOY NOTE: verify tenant datapoint assignments before production deploy —
   * tenants with DISABLE_FOREIGN_EXCHANGE but not DISABLE_WEB_SITE_TECHNOLOGIES
   * (or vice versa) will see different Foreign Exchange filter visibility.
   */
  const enableForeignExchange = computed(() => !isEnabled(DATAPOINT_CODES.DISABLE_FOREIGN_EXCHANGE))

  // §8.4 — New flags (19 from source plan §4.1 + 3 graduated/dropdown-helper)

  // List builder / people search counters & pagination
  const showContactCount              = computed(() => isEnabled(DATAPOINT_CODES.DISPLAY_CONTACT_COUNT))
  const enableCompanyCount            = computed(() => !isEnabled(DATAPOINT_CODES.DISABLE_COMPANY_COUNT))
  const enablePagination              = computed(() => !isEnabled(DATAPOINT_CODES.DISABLE_PAGINATION))
  const enableCompanyNameSearch       = computed(() => isEnabled(DATAPOINT_CODES.COMPANY_NAME_SEARCH))
  const enableContactListSortBy       = computed(() => isEnabled(DATAPOINT_CODES.ENABLE_CONTACT_LIST_SORT_BY))
  const enableMaxLimitPerList         = computed(() => !isEnabled(DATAPOINT_CODES.DISABLE_MAX_LIMIT_PER_LIST))
  const enableMaxLimitExport          = computed(() => isEnabled(DATAPOINT_CODES.ENABLE_MAX_LIMIT_EXPORT))

  // Contact actions
  const allowCopyCut                  = computed(() => isEnabled(DATAPOINT_CODES.ALLOW_COPY_CUT))
  const enableEmployeeHistory         = computed(() => isEnabled(DATAPOINT_CODES.ENABLE_EMPLOYEE_HISTORY))
  const enableDuplicateCreation       = computed(() => !isEnabled(DATAPOINT_CODES.DISABLE_DUPLICATE_CREATION))
  const enableContactAcquisitionOnDemand = computed(() => isEnabled(DATAPOINT_CODES.ENABLE_CONTACT_ACQUISITION_ON_DEMAND))

  // CRM / refresh / dedup
  const enableRefreshContacts         = computed(() => isEnabled(DATAPOINT_CODES.BULKDOWNLOAD_REFRESH_CONTACTS))
  const enableRefreshContactList      = computed(() => isEnabled(DATAPOINT_CODES.ENABLE_REFRESH_CONTACT_LIST))
  const enableRefreshContactOrchestrator = computed(() => isEnabled(DATAPOINT_CODES.ENABLE_REFRESH_CONTACT_ORCHESTRATOR))
  const enableRefreshContactRealTime  = computed(() => isEnabled(DATAPOINT_CODES.ENABLE_REFRESH_CONTACT_REAL_TIME))
  const enableSalesforceDedup         = computed(() => isEnabled(DATAPOINT_CODES.SALESFORCE_DEDUP))
  const enableMulticompaniesCrmExport = computed(() => !isEnabled(DATAPOINT_CODES.DISABLE_MULTICOMPANIES_CRM_EXPORT))

  // Filters / advanced
  const enableDpdCustomFilter         = computed(() => isEnabled(DATAPOINT_CODES.ENABLE_DPD_CUSTOM_FILTER))
  const enableNewMerchantData         = computed(() => isEnabled(DATAPOINT_CODES.ENABLE_NEW_MERCHANT_DATA))
  const enableCountryRestriction      = computed(() => isEnabled(DATAPOINT_CODES.COUNTRY_RESTRICTION))

  // §9 dropdown-helper flags (consumed by useDatapointDropdownFilters)
  const enableAdultContent            = computed(() => isEnabled(DATAPOINT_CODES.ENABLE_ADULT_CONTENT))
  const enableShippingCountryRestriction = computed(() => isEnabled(DATAPOINT_CODES.SHIPPING_COUNTRY_RESTRICTION))

  // §11 graduated from company-detail.store.ts
  const enableSimilarSites            = computed(() => isEnabled(DATAPOINT_CODES.ENABLE_SIMILAR_SITES))

  // Gates the configurable "Customize columns" picker on the search results tables.
  const enableSearchResultsColumns    = computed(() => isEnabled(DATAPOINT_CODES.ENABLE_SEARCH_RESULTS_COLUMNS))
  // Company Detail Contacts tab: "View People" + "Manual Refresh" buttons.
  const enableDisplayManualJob        = computed(() => isEnabled(DATAPOINT_CODES.DISPLAY_MANUAL_JOB_ON_CONTACT_SCREEN))

  // AI Assistant gating
  const enableAiAssistant = computed(() => isEnabled(DATAPOINT_CODES.ENABLE_AI_ASSISTANT))

  // Export control — inverted: true when file export is NOT disabled
  const enableFileExport = computed(() => !isEnabled(DATAPOINT_CODES.DISABLE_FILE_EXPORT))
  const requireCrmConnection = computed(() => isEnabled(DATAPOINT_CODES.REQUIRE_CRM_CONNECTION))
  const crmConnectionGateActive = computed(() =>
    datapointsReady.value && requireCrmConnection.value && userCanExportCRM.value && !crmIsConnected.value,
  )
  // True when the account strips no-email contacts on export (DEV-7315)
  const removeNoEmailOnExport = computed(() =>
    isEnabled(DATAPOINT_CODES.REMOVE_CONTACTS_WITH_NO_EMAIL_ON_EXPORT),
  )

  return {
    // Lifecycle
    loading, datapointsReady, fetchDatapoints, resetDatapoints,

    // Aux API state (§4.2) — isOrganicUser intentionally NOT exposed, see §4.3 bug #5
    canPurchase, canSetCRM, isTrialUser, isPlanExpired, planExpireDate,
    isCRMExportDisabled, userCanExportCRM, userCanExportFile, crmIsConnected,
    isBulkDownloadEnabled, restrictedCountries,

    // Business Location
    enablePhysicalLocation, enableInternationalOffices, enableBrandFilters, enableLocator,
    // Industry
    enableIndustry, enableSubIndustry, enableSic, enableNaic,
    // Contact
    enableDirectDialMobile,
    enableContactEmailStatus,
    // Business Sizing
    enableBusinessSizing, enableGlobalTraffic,
    // E-commerce
    enableEcommerce, enableShipping, enableInternationalShipping, enableMarketplaceSeller,
    enableForeignExchange,
    // Technologies
    enableWebsiteTechnologies, enableCloudHosting, enableEmailHosting,
    // Service Providers
    enablePayment, enableImportersExporters,
    // Advanced Search
    enableKeywordSearch, enableAuthorizedProvider, enableBusinessModel,
    enableCompaniesWithLocalizedSites, enableLocalizedDomains,
    enableLeadListFilters, enableCrmDataFilter,
    // Social Media
    enableSocialMedia,
    // CRM
    enableVerifyCrmData,
    // CRM Setup
    disableCrmExport, enableCrmDedupOption, enableIndustryMapping,
    enableSalesforceRefreshCompanies, enableSalesforceRefreshContacts,
    suppressContactData, enableHybridExport, enableVue3NewUi,
    // Lead Enrichment
    isDevTeam, enableLeadEnrichment, enableLeadScoring,
    // AI Search
    enableAiSearch,
    // Navigation Gating
    enableLookAlike, hidePeopleSearch, enablePreferredTitles, enableExternalSofoApi, enableBackOfficeIndex,

    // §8.4 new flags
    showContactCount, enableCompanyCount, enablePagination, enableCompanyNameSearch,
    enableContactListSortBy, enableMaxLimitPerList, enableMaxLimitExport,
    allowCopyCut, enableEmployeeHistory, enableDuplicateCreation,
    enableContactAcquisitionOnDemand,
    enableRefreshContacts, enableRefreshContactList, enableRefreshContactOrchestrator,
    enableRefreshContactRealTime, enableSalesforceDedup, enableMulticompaniesCrmExport,
    enableDpdCustomFilter, enableNewMerchantData, enableCountryRestriction,

    // §9 dropdown-helper flags
    enableAdultContent, enableShippingCountryRestriction,

    // §11 graduated flag
    enableSimilarSites,

    // Search results columns
    enableSearchResultsColumns,
    enableDisplayManualJob,

    // AI Assistant
    enableAiAssistant,
    // Export control
    enableFileExport, removeNoEmailOnExport, requireCrmConnection, crmConnectionGateActive,
  }
}
